// ABOUTME: The 5D layer: the bird's-eye sea, this crossing's compass, the aerial wa'a and the THIS CROSSING card.
// ABOUTME: It builds in on arrival from 5C and sails off by send-off stage, both read from the runner frame's screen motion.

import { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { useFrameScale } from '../../crossing/useFrameScale';
import { useCrossingReducedMotion } from '../../crossing/useReducedMotion';
import { useScreenMotion } from '../../runner/screenMotion';
import { AerialWaa } from './AerialWaa';
import { SummaryCard } from './SummaryCard';
import { SummaryCompass } from './SummaryCompass';
import { SummarySea, SUMMARY_HORIZON_TOP } from './SummarySea';
import type { Summary } from './summaryModel';

const DESKTOP_WIDTH = 1080;
const PHONE_WIDTH = 390;
const HORIZON_BELOW_TOP = 142; // SPEC section 12: the horizon sits 142 below the top of the scene
const FINISHED_STAGE = 3;
const CANOE_SAILED_OFF = 'translate(402px, -179px) rotate(-24deg)';

// The build-in's beats, ms after arrival: the layer arrives, the highlighted compass replaces the faint one, the card slides in.
const BUILD_IN_BEATS_MS = [60, 2600, 3400];

type BuildInStage = 0 | 1 | 2 | 3;

function useBuildInStage(animate: boolean): BuildInStage {
  const [stage, setStage] = useState<BuildInStage>(animate ? 0 : FINISHED_STAGE);
  useEffect(() => {
    if (!animate) return;
    const timers = BUILD_IN_BEATS_MS.map((ms, index) => window.setTimeout(() => setStage((index + 1) as BuildInStage), ms));
    return () => timers.forEach(window.clearTimeout);
  }, [animate]);
  return stage;
}

interface SummarySceneProps {
  summary: Summary;
}

export function SummaryScene({ summary }: SummarySceneProps) {
  const { buildIn, sendOffStage } = useScreenMotion();
  const reducedMotion = useCrossingReducedMotion();
  const desktop = useFrameScale(DESKTOP_WIDTH);
  const phone = useFrameScale(PHONE_WIDTH);
  const mob = desktop.mob;
  const worldWidth = mob ? PHONE_WIDTH : DESKTOP_WIDTH;
  const { frameSc } = mob ? phone : desktop;
  const pc = useBuildInStage(buildIn && !reducedMotion);
  const sailingOff = sendOffStage >= 3;

  return (
    <Box
      data-testid="summary-scene"
      data-build-in-stage={pc}
      data-send-off-stage={sendOffStage}
      sx={{
        position: 'absolute', inset: 0, overflow: 'hidden', zIndex: 3, pointerEvents: 'none',
        opacity: pc >= 1 ? 1 : 0,
        transform: pc >= 1 ? 'none' : 'scale(1.9) translate(90px, -40px)',
        transformOrigin: '29% 61%',
        transition: 'transform 2s cubic-bezier(.2,.7,.2,1) .5s, opacity 1.2s ease .5s',
      }}
    >
      <Box
        sx={{
          position: 'absolute', left: '50%', top: 0, width: worldWidth, height: '100%', marginLeft: `-${worldWidth / 2}px`,
          transform: `scale(${frameSc})`, transformOrigin: 'top center',
        }}
      >
        <Box aria-hidden="true" sx={{ position: 'absolute', inset: 0 }}>
          <Box sx={{ position: 'absolute', inset: 0, top: HORIZON_BELOW_TOP - SUMMARY_HORIZON_TOP }}>
            <SummarySea reducedMotion={reducedMotion} />
          </Box>
          <Box sx={{ position: 'absolute', left: 28, top: 116, width: 600, height: 600, zIndex: 4, transform: 'rotate(66deg) scale(0.88)', transformOrigin: '50% 50%' }}>
            <Box
              data-testid="summary-compass-faint-art"
              sx={{
                position: 'absolute', left: -20, top: -24, width: 640, height: 644,
                opacity: pc === 1 ? 1 : 0,
                transition: pc === 1 ? 'opacity .7s ease 1.7s' : 'opacity .8s ease',
              }}
            >
              <Box
                component="img"
                src="/crossing/1c-compass-unhighlighted.svg"
                alt=""
                sx={{
                  position: 'absolute', left: 0, top: 0, width: 640, height: 644,
                  '@keyframes pvCompassBreathe': { '0%, 100%': { transform: 'scale(1)', opacity: 0.9 }, '50%': { transform: 'scale(1.012)', opacity: 1 } },
                  animation: reducedMotion ? 'none' : 'pvCompassBreathe 8s ease-in-out infinite',
                }}
              />
            </Box>
            <Box
              data-testid="summary-compass-art"
              sx={{ position: 'absolute', left: -20, top: -24, width: 640, height: 644, opacity: pc >= 2 ? 1 : 0, transition: 'opacity .8s ease' }}
            >
              <SummaryCompass summary={summary} ringsFaded={sendOffStage >= 1} starScaled={sendOffStage >= 2} />
            </Box>
          </Box>
          <Box
            data-testid="summary-canoe"
            sx={{
              position: 'absolute', left: 218, top: 341, width: 220, height: 150, zIndex: 6,
              transform: sailingOff ? CANOE_SAILED_OFF : 'rotate(-24deg)', transformOrigin: '50% 50%',
              transition: 'transform 2.6s cubic-bezier(.45,0,.55,1)',
            }}
          >
            <Box sx={{ position: 'absolute', left: -215, top: -40 }}>
              <AerialWaa unfurled={sendOffStage >= 1} wakes={sendOffStage >= 1} reducedMotion={reducedMotion} />
            </Box>
          </Box>
        </Box>
        <SummaryCard
          summary={summary}
          hidden={mob}
          beat={{
            opacity: pc >= FINISHED_STAGE && !sailingOff ? 1 : 0,
            transform: pc >= FINISHED_STAGE ? 'none' : 'translateX(40px)',
            transition: 'opacity .6s ease, transform .6s cubic-bezier(.2,.7,.2,1)',
          }}
        />
      </Box>
    </Box>
  );
}
